import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import Layout from '../components/Layout';
import { 
  Package, 
  ArrowLeft, 
  CreditCard,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  Briefcase,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Orders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [addingToCrmId, setAddingToCrmId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadOrders();
  }, [user, navigate]);

  const loadOrders = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this pending order?')) return;
    
    setDeletingId(orderId);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: "Order deleted",
          description: "The pending order has been removed"
        });
        setOrders(orders.filter(o => o.id !== orderId));
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete order');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddToCRM = async (orderId) => {
    setAddingToCrmId(orderId);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/orders/${orderId}/add-to-crm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Added to CRM",
          description: `Order added to ${data.stage_name || 'CRM'} stage`
        });
        // Update local state to reflect the change
        setOrders(orders.map(o => 
          o.id === orderId ? { ...o, added_to_crm: true } : o
        ));
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add to CRM');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setAddingToCrmId(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      processing: { label: 'Processing', color: 'bg-purple-100 text-purple-800', icon: Package },
      shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
      delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    return configs[status] || configs.pending;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/shop')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Shop
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
          </div>

          {orders.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-medium text-gray-700 mb-2">No orders yet</h2>
                <p className="text-gray-500 mb-6">Start shopping to see your orders here</p>
                <Button onClick={() => navigate('/shop')}>
                  Browse Products
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const statusConfig = getStatusConfig(order.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card key={order.id}>
                    <CardContent className="p-6">
                      {/* Order Header */}
                      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-gray-900">
                              Order #{order.order_number}
                            </h3>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            Placed on {formatDate(order.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Total</p>
                          <p className="text-lg font-bold text-amber-600">
                            {formatPrice(order.total)}
                          </p>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="space-y-3 mb-4">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex gap-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              {item.product_image ? (
                                <img 
                                  src={item.product_image} 
                                  alt={item.product_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <CreditCard className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.product_name}</p>
                              <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                              <p className="text-sm font-medium text-gray-900 mt-1">
                                {formatPrice(item.item_total)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Delivery Address */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Delivery Address</p>
                        <p className="text-sm text-gray-600">
                          {order.address.full_name}, {order.address.phone}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.address.address_line1}
                          {order.address.address_line2 && `, ${order.address.address_line2}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.address.city}, {order.address.state} - {order.address.pincode}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                        {/* Delete button - only for pending orders */}
                        {order.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteOrder(order.id)}
                            disabled={deletingId === order.id}
                            data-testid={`delete-order-${order.id}`}
                          >
                            {deletingId === order.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Delete Order
                          </Button>
                        )}
                        
                        {/* Add to CRM button - only for paid/confirmed orders that haven't been added */}
                        {order.payment_status === 'paid' && !order.added_to_crm && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:bg-blue-50 border-blue-200"
                            onClick={() => handleAddToCRM(order.id)}
                            disabled={addingToCrmId === order.id}
                            data-testid={`add-to-crm-${order.id}`}
                          >
                            {addingToCrmId === order.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Briefcase className="w-4 h-4 mr-2" />
                            )}
                            Add to CRM
                          </Button>
                        )}
                        
                        {/* Show badge if already added to CRM */}
                        {order.added_to_crm && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Added to CRM
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Orders;
